import React, { useState } from 'react';
import BarInterface from './BarInterface';
import DrinkManager from './DrinkManager';
import PersonManager from './PersonManager';
import ProtectedAdminPanel from './ProtectedAdminPanel';
import { ArrowLeft } from 'lucide-react';
import './EmployeeInterface.css';

interface Drink {
  id: number;
  name: string;
  basePrice: number;
  currentPrice: number;
  icon: string;
  purchases: number;
  priceHistory: { price: number; timestamp: string }[];
}

interface Sale {
  id: number;
  drinkId: number;
  drinkName: string;
  quantity: number;
  priceAtSale: number;
  totalCost: number;
  timestamp: string;
  personId?: number;
  personName?: string;
}

interface Person {
  id: number;
  name: string;
  totalSpent: number;
  totalSavings: number;
  purchaseCount: number;
  createdAt: string;
  qrCode?: string;
  qrCodeData?: string;
}

interface EmployeeInterfaceProps {
  drinks: Drink[];
  salesHistory: Sale[];
  persons: Person[];
  onPurchase: (drinkId: number, quantity: number, personId?: number) => Promise<any>;
  onAddDrink: (name: string, basePrice: number, icon: string) => Promise<any>;
  onUpdateBasePrice: (drinkId: number, basePrice: number) => Promise<any>;
  onDeleteDrink: (drinkId: number) => Promise<any>;
  onAddPerson: (name: string) => Promise<any>;
  onDeletePerson: (personId: number) => Promise<any>;
  onBack: () => void;
}

const EmployeeInterface: React.FC<EmployeeInterfaceProps> = ({
  drinks,
  salesHistory,
  persons,
  onPurchase,
  onAddDrink,
  onUpdateBasePrice,
  onDeleteDrink,
  onAddPerson,
  onDeletePerson,
  onBack
}) => {
  const [activeSection, setActiveSection] = useState<'bar' | 'drinks' | 'persons' | 'admin'>('bar');

  // Data refresh is handled by Socket.io in parent App component

  const renderContent = () => {
    switch (activeSection) {
      case 'bar':
        return (
          <BarInterface 
            drinks={drinks} 
            salesHistory={salesHistory} 
            persons={persons} 
            onPurchase={onPurchase}
          />
        );
      case 'drinks':
        return (
          <DrinkManager 
            drinks={drinks} 
            onAddDrink={onAddDrink}
            onUpdateBasePrice={onUpdateBasePrice}
            onDeleteDrink={onDeleteDrink}
          />
        );
      case 'persons':
        return (
          <PersonManager 
            persons={persons}
            onAddPerson={onAddPerson}
            onDeletePerson={onDeletePerson}
          />
        );
      case 'admin':
        return <ProtectedAdminPanel />;
      default:
        return null;
    }
  };

  return (
    <div className="employee-interface">
      <div className="employee-header">
        <button onClick={onBack} className="back-button">
          <ArrowLeft size={20} />
          Zurück zur Gäste-Ansicht
        </button>
        <h1>🔒 Mitarbeiter-Bereich</h1>
      </div>

      <nav className="employee-nav">
        <button 
          className={`nav-button ${activeSection === 'bar' ? 'active' : ''}`}
          onClick={() => setActiveSection('bar')}
        >
          🍺 Bar
        </button>
        <button 
          className={`nav-button ${activeSection === 'drinks' ? 'active' : ''}`}
          onClick={() => setActiveSection('drinks')}
        >
          🛠️ Getränke
        </button>
        <button 
          className={`nav-button ${activeSection === 'persons' ? 'active' : ''}`}
          onClick={() => setActiveSection('persons')}
        >
          👥 Personen
        </button>
        <button 
          className={`nav-button ${activeSection === 'admin' ? 'active' : ''}`}
          onClick={() => setActiveSection('admin')}
        >
          ⚙️ Admin
        </button>
      </nav>

      <main className="employee-content">
        {renderContent()}
      </main>
    </div>
  );
};

export default EmployeeInterface;